import { AnimatePresence, motion } from "motion/react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  amortizationMethodsListRealEstate,
  isPaymentFrequencyConfigurable,
  paymentFrequencyList,
} from "utils";
import type { BidPostData } from "validations";

import {
  TotalCostTooltip,
  totalCostLabelText,
} from "@krea/lender-features/components/total-cost-label";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { FieldLabel } from "@krea/shared/design-system/input/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "@krea/shared/design-system/input/input-group";
import { IntegerInput } from "@krea/shared/design-system/input/integer-input";
import { MoneyInput } from "@krea/shared/design-system/input/money-input";
import { PercentageInput } from "@krea/shared/design-system/input/percentage-input";
import { PaymentFrequencySelect } from "@krea/shared/design-system/PaymentFrequencySelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import { ControlledEarlyRepaymentFeeInput } from "@krea/shared/features/early-repayment";
import type { Currency } from "@krea/shared/types/common";

import {
  PaymentFrequency,
  RealEstateAmortizationMethodEnum,
} from "@/features/common/enums";
import type { IRealEstateBidInput } from "@/features/lender-responses/types/lender-response";

import DetailsItem from "../../../../details/item";
import {
  AdministrationFeeTooltip,
  administrationFeeLabelText,
} from "../../bidHelpers";
import { lenderMonthlyInputVariants } from "../shared/animations";

function AmortizationMethodSelect({
  name,
  value,
  onChange,
  onBlur,
  isSubmitting,
  filteredOptions,
  placeholder,
}: {
  name: string;
  value: RealEstateAmortizationMethodEnum;
  onChange: (v: RealEstateAmortizationMethodEnum) => void;
  onBlur: () => void;
  isSubmitting: boolean;
  filteredOptions: { value: RealEstateAmortizationMethodEnum; label: string }[];
  placeholder: string;
}) {
  return (
    <Select
      value={String(value ?? "")}
      onValueChange={(v) => onChange(v as RealEstateAmortizationMethodEnum)}
      onOpenChange={(open) => {
        if (!open) onBlur();
      }}
      disabled={isSubmitting}
      required
    >
      <SelectTrigger id={name}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {filteredOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface LoanFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean; // Is editing existing offer or creating new
  isInterestRatePerYearActivated?: boolean;
}

export const RealEstateBidForm = ({
  bidFormIndex,
  currency,
  isEditing,
  isInterestRatePerYearActivated,
}: LoanFormProps) => {
  const {
    control,
    setValue,
    trigger,
    formState: { isSubmitting },
  } = useFormContext<BidPostData<IRealEstateBidInput>>();

  const bid = useWatch({
    control,
    name: `bids.${bidFormIndex}`,
  });

  const handleAmortizationMethodChange = (
    oldValue: RealEstateAmortizationMethodEnum,
    newValue?: RealEstateAmortizationMethodEnum,
  ) => {
    if (
      oldValue === RealEstateAmortizationMethodEnum.LINEAR &&
      newValue !== RealEstateAmortizationMethodEnum.LINEAR
    ) {
      setValue(`bids.${bidFormIndex}.amortizationInPercent`, undefined);
    }

    if (!isPaymentFrequencyConfigurable(newValue)) {
      setValue(
        `bids.${bidFormIndex}.paymentFrequency`,
        PaymentFrequency.MONTHLY,
        {
          shouldValidate: true,
        },
      );
    }
  };

  return (
    <div className="flex w-full flex-col">
      <div className="w-full space-y-4">
        <DetailsItem
          title={isEditing ? "Update real estate terms" : "Real estate terms"}
          className="mb-4"
        >
          <div className="w-full space-y-4">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.amount`}
              label="Amount"
              required
              render={({ field }) => (
                <MoneyInput
                  {...field}
                  currency={currency}
                  id={field.name}
                  onChange={(v) => {
                    field.onChange(v);
                    // The approved amount is the base of the total-cost minimum,
                    // so every fee rule keyed on it has to be re-checked.
                    trigger([
                      `bids.${bidFormIndex}.totalCost`,
                      `bids.${bidFormIndex}.setupFee`,
                      `bids.${bidFormIndex}.administrationFees`,
                    ]);
                  }}
                />
              )}
            />
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.paybackTimeMonths`}
              label="Loan term"
              required
              render={({ field }) => (
                <InputGroup>
                  <IntegerInput
                    {...field}
                    id={field.name}
                    className="flex-1 rounded-none border-0 bg-transparent shadow-none ring-0 focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0"
                    onChange={(v) => {
                      field.onChange(v);
                      // The term multiplies the administration fees inside the
                      // total-cost minimum, so re-check it when the term changes.
                      trigger([
                        `bids.${bidFormIndex}.totalCost`,
                        `bids.${bidFormIndex}.setupFee`,
                        `bids.${bidFormIndex}.administrationFees`,
                      ]);
                    }}
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupText>months</InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              )}
            />
          </div>
        </DetailsItem>

        <DetailsItem className="mb-4">
          <div className="w-full space-y-4">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.amortizationMethod`}
              label="Amortisation method"
              required
              render={({ field }) => (
                <AmortizationMethodSelect
                  name={field.name}
                  value={field.value}
                  onChange={(v) => {
                    handleAmortizationMethodChange(field.value, v);
                    field.onChange(v);
                  }}
                  onBlur={field.onBlur}
                  isSubmitting={isSubmitting}
                  filteredOptions={amortizationMethodsListRealEstate}
                  placeholder="Select amortization method"
                />
              )}
            />

            {isPaymentFrequencyConfigurable(bid?.amortizationMethod) && (
              <ControlledWrapper
                control={control}
                name={`bids.${bidFormIndex}.paymentFrequency`}
                label="Payment frequency"
                required
                render={({ field }) => (
                  <PaymentFrequencySelect
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isSubmitting={isSubmitting}
                    options={paymentFrequencyList}
                    placeholder="Select payment frequency"
                  />
                )}
              />
            )}
          </div>
        </DetailsItem>

        <DetailsItem className="mb-4" title="">
          <div className="w-full">
            <ControlledWrapper
              control={control}
              name={`bids.${bidFormIndex}.totalCost`}
              render={({ field }) => (
                <>
                  <div className="flex items-center gap-2">
                    <FieldLabel htmlFor={field.name}>
                      {totalCostLabelText} <span className="text-red">*</span>
                    </FieldLabel>
                    <TotalCostTooltip />
                  </div>
                  <MoneyInput
                    {...field}
                    currency={currency}
                    id={field.name}
                    aria-required
                    onChange={(v) => {
                      field.onChange(v);
                      trigger([
                        `bids.${bidFormIndex}.totalCost`,
                        `bids.${bidFormIndex}.setupFee`,
                        `bids.${bidFormIndex}.administrationFees`,
                      ]);
                    }}
                  />
                </>
              )}
            />
          </div>
        </DetailsItem>

        <DetailsItem title="" className="mb-4">
          <div className="w-full space-y-4">
            <span className="mb-2 ml-3 block text-sm italic text-gray-600">
              {'Here you can explain what the "Total cost" consists of:'}
            </span>
            <div className="w-full">
              <div className="mb-0 ml-3 space-y-4 border-l pl-3">
                <AnimatePresence mode="wait" initial={false}>
                  {bid.amortizationMethod ===
                    RealEstateAmortizationMethodEnum.LINEAR && (
                    <motion.div
                      key="amortizationInPercent"
                      initial="initial"
                      exit="exit"
                      animate="animate"
                      variants={lenderMonthlyInputVariants}
                    >
                      <ControlledWrapper
                        control={control}
                        name={`bids.${bidFormIndex}.amortizationInPercent`}
                        label="Amortisation in % of loan amount"
                        required
                        render={({ field }) => (
                          <PercentageInput
                            {...field}
                            id={field.name}
                            placeholder="Value in %"
                          />
                        )}
                      />
                    </motion.div>
                  )}
                  <DetailsItem className="mb-4" key="lender_monthly_interest">
                    <div className="w-full">
                      <motion.div
                        key="lender_monthly_interest"
                        initial="initial"
                        exit="exit"
                        animate="animate"
                        variants={lenderMonthlyInputVariants}
                      >
                        <ControlledWrapper
                          control={control}
                          name={`bids.${bidFormIndex}.lenderMonthlyInterest`}
                          label={
                            isInterestRatePerYearActivated
                              ? "Interest per year"
                              : "Interest per 30 days"
                          }
                          required
                          render={({ field }) => (
                            <PercentageInput
                              {...field}
                              id={field.name}
                              placeholder="Value in %"
                            />
                          )}
                        />
                      </motion.div>
                    </div>
                  </DetailsItem>
                </AnimatePresence>

                <ControlledWrapper
                  control={control}
                  name={`bids.${bidFormIndex}.setupFee`}
                  label="Setup fee"
                  render={({ field }) => (
                    <MoneyInput
                      {...field}
                      currency={currency}
                      id={field.name}
                      onChange={(v) => {
                        field.onChange(v);
                        trigger([
                          `bids.${bidFormIndex}.totalCost`,
                          `bids.${bidFormIndex}.setupFee`,
                          `bids.${bidFormIndex}.administrationFees`,
                        ]);
                      }}
                    />
                  )}
                />

                {/* NOT keyed by bid identity, though it should be: the comment's
                    revealed state is component-local, and these bid lists are keyed by
                    array index, so removing a bid slides another into the slot and it
                    inherits that state. No form shape carries a bid id to key on —
                    the write types deliberately omit it — so the fix is a stable
                    per-bid key owned by the list, not something reachable here. */}
                <ControlledEarlyRepaymentFeeInput
                  control={control}
                  bidFormIndex={bidFormIndex}
                  currency={currency}
                />

                <ControlledWrapper
                  control={control}
                  name={`bids.${bidFormIndex}.administrationFees`}
                  render={({ field }) => (
                    <>
                      <div className="flex items-center gap-2">
                        <FieldLabel>{administrationFeeLabelText}</FieldLabel>
                        <AdministrationFeeTooltip />
                      </div>
                      <MoneyInput
                        {...field}
                        currency={currency}
                        id={field.name}
                        onChange={(v) => {
                          field.onChange(v);
                          // Total cost must cover these fees over the whole
                          // term, so re-check it when they change.
                          trigger([
                            `bids.${bidFormIndex}.totalCost`,
                            `bids.${bidFormIndex}.setupFee`,
                            `bids.${bidFormIndex}.administrationFees`,
                          ]);
                        }}
                      />
                    </>
                  )}
                />
              </div>
            </div>
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
